import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { useTranslation } from "react-i18next";
import { getHashedIP } from "./utils";
import { moderateUsername } from "./utils/contentModeration";
import { signInWithGoogle } from "./utils/googleAuth";
import { signInWithApple } from "./utils/appleAuth";
import {
  signInWithEmail,
  signOutUser,
  sendPasswordReset,
} from "./utils/authHelpers";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false); // Added loading state

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true); // Set loading to true on submit
    try {
      const result = await signInWithEmail(email, password);
      navigate("/");
    } catch (err) {
      setError(getFriendlyErrorMessage(err.code));
    } finally {
      setLoading(false); // Set loading to false after operation
    }
  };

  const handleOAuthLogin = async (provider, providerName) => {
    setError("");
    setLoading(true);

    try {
      console.log(`🚀 Avvio login ${providerName}...`);
      const result = await provider();

      if (!result || !result.user) {
        console.error(`❌ Nessun utente restituito da ${providerName}`);
        setError(t(`auth.errors.${providerName === 'Google' ? 'google' : 'apple'}LoginError`));
        return;
      }

      const user = result.user;
      console.log(`✅ ${providerName} login successful:`, user.email);

      // Con skipNativeAuth: true, l'autenticazione avviene già sul JS SDK in appleAuth.js/googleAuth.js
      // Non serve più sincronizzare - l'utente è già autenticato sul JS SDK!
      console.log(`🔑 [Firestore] User UID: ${user.uid}`);
      console.log(`🔑 [Firestore] User email: ${user.email}`);
      console.log(`🔑 [Firestore] User displayName: ${user.displayName}`);

      // ========== PRIMA OPERAZIONE: Verifica/Crea utente in Firestore ==========
      const userRef = doc(db, "users", user.uid);
      const userPath = `users/${user.uid}`;
      
      console.log(`📖 [Firestore] getDoc su path: ${userPath}`);
      
      let userSnap;
      try {
        userSnap = await getDoc(userRef);
        console.log(`✅ [Firestore] getDoc OK - exists: ${userSnap.exists()}`);
      } catch (getDocError) {
        console.error(`❌ [Firestore] ERRORE getDoc su path: ${userPath}`);
        console.error(`❌ [Firestore] Error code:`, getDocError.code);
        console.error(`❌ [Firestore] Error message:`, getDocError.message);
        console.error(`❌ [Firestore] Error full:`, JSON.stringify(getDocError));
        throw getDocError;
      }

      if (!userSnap.exists()) {
        // ========== NUOVO UTENTE ==========
        console.log(`🆕 [Firestore] Utente non esiste, creo nuovo profilo...`);
        
        // Controlli IP (opzionale, con try/catch separato)
        let ipHash = null;
        try {
          ipHash = await getHashedIP();
          if (ipHash) {
            console.log(`🔍 [Firestore] Verifica IP: ${ipHash}`);
            const ipPath = `ip_addresses/${ipHash}`;
            
            try {
              console.log(`📖 [Firestore] getDoc su path: ${ipPath}`);
              const ipDoc = await getDoc(doc(db, "ip_addresses", ipHash));
              const createdCount = ipDoc.exists() ? ipDoc.data().createdAccounts || 0 : 0;
              console.log(`✅ [Firestore] getDoc IP OK - count: ${createdCount}`);

              if (createdCount >= 5) {
                console.log(`🔒 [Firestore] Limite IP raggiunto (${createdCount}/5)`);
                await signOutUser();
                setError(t("auth.errors.ipLimitReached"));
                return;
              }

              // Incrementa contatore IP
              console.log(`📝 [Firestore] setDoc su path: ${ipPath}`);
              await setDoc(
                doc(db, "ip_addresses", ipHash),
                {
                  createdAccounts: createdCount + 1,
                  lastCreated: new Date().toISOString(),
                },
                { merge: true },
              );
              console.log(`✅ [Firestore] setDoc IP OK`);
            } catch (ipError) {
              console.error(`❌ [Firestore] ERRORE operazione IP su path: ${ipPath}`);
              console.error(`❌ [Firestore] IP Error code:`, ipError.code);
              console.error(`❌ [Firestore] IP Error message:`, ipError.message);
              console.warn(`⚠️ [Firestore] Procedo comunque senza IP check`);
            }
          }
        } catch (ipHashError) {
          console.error(`❌ [Firestore] Errore getHashedIP:`, ipHashError);
          console.warn(`⚠️ [Firestore] Procedo senza IP hash`);
        }

        // Moderazione display name
        let validDisplayName = user.email ? user.email.split("@")[0] : "User";
        if (user.displayName) {
          try {
            console.log(`🛡️ [Moderation] Moderazione display name: ${user.displayName}`);
            const moderationResult = await moderateUsername(user.displayName);
            if (moderationResult.allowed) {
              validDisplayName = user.displayName;
              console.log(`✅ [Moderation] Display name approvato`);
            } else {
              console.log(`🚫 [Moderation] Display name bloccato, uso fallback: ${validDisplayName}`);
            }
          } catch (moderationError) {
            console.error(`❌ [Moderation] Errore:`, moderationError);
          }
        }

        // ========== CREA/AGGIORNA PROFILO UTENTE (sempre con merge: true) ==========
        const newUserData = {
          uid: user.uid,
          email: user.email || null,
          displayName: user.displayName || validDisplayName,
          provider: user.providerData?.[0]?.providerId || providerName.toLowerCase(),
          lastLogin: serverTimestamp(),
          avatar: "",
          following: [],
          followers: [],
          language: "it",
          isPremium: false,
          isNewUser: true,
          ipHash: ipHash || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        console.log(`📝 [Firestore] setDoc (CREATE with merge:true) su path: ${userPath}`);
        console.log(`📝 [Firestore] Data:`, JSON.stringify({
          ...newUserData,
          lastLogin: "serverTimestamp()",
          createdAt: "serverTimestamp()",
          updatedAt: "serverTimestamp()",
        }, null, 2));

        try {
          await setDoc(userRef, newUserData, { merge: true });
          console.log(`✅ [Firestore] setDoc CREATE OK (merge:true) - Nuovo utente ${providerName} creato!`);
        } catch (setDocError) {
          console.error(`❌ [Firestore] ERRORE setDoc CREATE su path: ${userPath}`);
          console.error(`❌ [Firestore] Error code:`, setDocError.code);
          console.error(`❌ [Firestore] Error message:`, setDocError.message);
          console.error(`❌ [Firestore] Error name:`, setDocError.name);
          console.error(`❌ [Firestore] Error full:`, JSON.stringify(setDocError));
          throw setDocError;
        }

      } else {
        // ========== UTENTE ESISTENTE (sempre con merge: true) ==========
        console.log(`👤 [Firestore] Utente già esiste, aggiorno con merge:true...`);
        
        const updateData = {
          uid: user.uid,
          email: user.email || null,
          displayName: user.displayName || '',
          provider: user.providerData?.[0]?.providerId || providerName.toLowerCase(),
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        console.log(`📝 [Firestore] setDoc (UPDATE with merge:true) su path: ${userPath}`);
        console.log(`📝 [Firestore] Data: uid=${user.uid}, provider=${user.providerData?.[0]?.providerId || providerName.toLowerCase()}`);

        try {
          await setDoc(userRef, updateData, { merge: true });
          console.log(`✅ [Firestore] setDoc UPDATE OK (merge:true) - Profilo aggiornato!`);
        } catch (updateError) {
          console.error(`❌ [Firestore] ERRORE setDoc UPDATE su path: ${userPath}`);
          console.error(`❌ [Firestore] Error code:`, updateError.code);
          console.error(`❌ [Firestore] Error message:`, updateError.message);
          console.error(`❌ [Firestore] Error name:`, updateError.name);
          console.error(`❌ [Firestore] Error full:`, JSON.stringify(updateError));
          throw updateError;
        }
      }

      console.log(`🎉 [${providerName}] Login e Firestore completati! Navigo a /`);
      navigate("/");

    } catch (error) {
      console.error(`❌ [${providerName}] ERRORE GENERALE:`);
      console.error(`❌ Error:`, error);
      console.error(`❌ Error code:`, error.code);
      console.error(`❌ Error message:`, error.message);
      console.error(`❌ Error name:`, error.name);
      try {
        console.error(`❌ Error JSON:`, JSON.stringify(error));
      } catch (e) {
        console.error(`❌ Error non serializzabile`);
      }
      setError(t(`auth.errors.${providerName === 'Google' ? 'google' : 'apple'}LoginError`));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    await handleOAuthLogin(signInWithGoogle, "Google");
  };

  const handleAppleLogin = async () => {
    await handleOAuthLogin(signInWithApple, "Apple");
  };

  const handleResetPassword = async () => {
    setLoading(true);
    setError("");
    try {
      await sendPasswordReset(resetEmail);
      setShowResetModal(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setError(
        getFriendlyErrorMessage(error.code) || t("auth.errors.emailError"),
      );
    } finally {
      setLoading(false);
    }
  };

  const getFriendlyErrorMessage = (code) => {
    switch (code) {
      case "auth/invalid-email":
        return t("auth.errors.invalidEmail");
      case "auth/user-not-found":
        return t("auth.errors.userNotFound");
      case "auth/wrong-password":
        return t("auth.errors.wrongPassword");
      case "auth/too-many-requests":
        return t("auth.errors.tooManyRequests");
      default:
        return t("auth.errors.general");
    }
  };

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>{t("hero.welcomeTitle")}</h1>
        <p style={styles.heroSubtitle}>{t("hero.welcomeSubtitle")}</p>
      </div>

      <div style={styles.box}>
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="email"
            placeholder={t("auth.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            disabled={loading}
          />
          <div style={styles.passwordWrapper}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t("auth.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ ...styles.input, paddingRight: "60px" }}
              disabled={loading}
            />
            <span
              style={styles.showPassword}
              onClick={() => !loading && setShowPassword(!showPassword)}
            >
              {showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
            </span>
          </div>
          <div
            style={styles.forgotPassword}
            onClick={() => !loading && setShowResetModal(true)}
          >
            {t("auth.login.forgotPassword")}
          </div>
          <button type="submit" style={styles.primaryButton} disabled={loading}>
            {loading ? t("auth.loggingIn") : t("auth.login.loginButton")}
          </button>
        </form>
        {error && <div style={styles.error}>{error}</div>}
        <button
          onClick={handleGoogleLogin}
          style={styles.googleButton}
          disabled={loading}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            style={{ marginRight: 10 }}
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {loading ? t("auth.googleLoggingIn") : t("auth.continueWithGoogle")}
        </button>
        <button
          onClick={handleAppleLogin}
          style={styles.appleButton}
          disabled={loading}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 170 170"
            style={{ marginRight: 10 }}
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929 0.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002 0.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-0.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375a25.222 25.222 0 0 1-0.188-3.07c0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71 0.12 1.083 0.17 2.166 0.17 3.24z"/>
          </svg>
          {loading ? t("auth.appleLoggingIn") : t("auth.continueWithApple")}
        </button>
        <p style={styles.toggleText}>
          {t("auth.login.noAccount")}{" "}
          <span
            style={styles.link}
            onClick={() => !loading && navigate("/signup")}
          >
            {t("auth.login.signUpLink")}
          </span>
        </p>
        <div style={styles.footer}>
          <Link to="/privacy" style={styles.footerLink}>
            {t("navigation.privacy")}
          </Link>
          <Link to="/terms" style={styles.footerLink}>
            {t("navigation.terms")}
          </Link>
        </div>
      </div>

      {showResetModal && (
        <div
          style={styles.overlay}
          onClick={() => !loading && setShowResetModal(false)}
        >
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 16 }}>{t("auth.resetPassword")}</h3>
            <input
              type="email"
              placeholder={t("auth.email")}
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
            <button
              style={styles.modalButton}
              onClick={handleResetPassword}
              disabled={loading}
            >
              {loading ? t("auth.sendingEmail") : t("common.confirm")}
            </button>
            <button
              style={styles.cancelButton}
              onClick={() => !loading && setShowResetModal(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {showSuccess && <div style={styles.toast}>{t("auth.emailSent")}</div>}
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "Poppins, sans-serif",
    padding: "16px",
    margin: "0 auto",
    maxWidth: "400px",
  },
  hero: {
    textAlign: "center",
    marginBottom: "24px",
    maxWidth: "100%",
    width: "100%",
  },
  heroTitle: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#000",
    marginBottom: "6px",
    lineHeight: "1.3",
    fontFamily: "Poppins, sans-serif",
    textAlign: "center",
  },
  heroSubtitle: {
    fontSize: "14px",
    color: "#555",
    marginBottom: "0",
    lineHeight: "1.5",
    margin: 0,
    fontWeight: "400",
    fontFamily: "Poppins, sans-serif",
    textAlign: "center",
  },
  box: {
    backgroundColor: "#f9f9f9",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
    width: "100%",
    textAlign: "center",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "16px",
  },
  input: {
    padding: "12px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    width: "100%",
    boxSizing: "border-box",
  },
  passwordWrapper: {
    position: "relative",
  },
  showPassword: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "12px",
    color: "#333",
    cursor: "pointer",
  },
  forgotPassword: {
    textAlign: "left",
    fontSize: "12px",
    color: "#333",
    cursor: "pointer",
    marginBottom: "6px",
  },
  primaryButton: {
    padding: "12px",
    backgroundColor: "#111",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  googleButton: {
    marginTop: "8px",
    padding: "12px",
    backgroundColor: "#fff",
    color: "#444",
    border: "1px solid #ccc",
    borderRadius: "6px",
    fontWeight: "500",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    width: "100%",
    marginBottom: "8px",
  },
  appleButton: {
    marginTop: "8px",
    padding: "12px",
    backgroundColor: "#fff",
    color: "#000",
    border: "1px solid #ccc",
    borderRadius: "6px",
    fontWeight: "500",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    width: "100%",
    marginBottom: "16px",
  },
  toggleText: {
    marginTop: "20px",
    fontSize: "16px",
    textAlign: "center",
    color: "#666",
    fontWeight: "500",
  },
  link: {
    color: "#111",
    cursor: "pointer",
    fontWeight: "bold",
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginTop: "20px",
    fontSize: "12px",
  },
  footerLink: {
    color: "#111",
    cursor: "pointer",
    textDecoration: "underline",
  },
  error: {
    color: "#dc2626",
    fontSize: "14px",
    textAlign: "center",
    marginTop: "10px",
    marginBottom: "10px",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  modal: {
    backgroundColor: "#fff",
    padding: "24px",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "380px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    textAlign: "center",
  },
  modalButton: {
    background: "#111",
    color: "#fff",
    padding: "12px",
    border: "none",
    borderRadius: "6px",
    width: "100%",
    cursor: "pointer",
    marginTop: "10px",
    marginBottom: "10px",
    fontSize: "14px",
  },
  cancelButton: {
    background: "#aaa",
    color: "#fff",
    padding: "12px",
    border: "none",
    borderRadius: "6px",
    width: "100%",
    cursor: "pointer",
    marginTop: "10px",
  },
  toast: {
    position: "fixed",
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#111",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: "8px",
    zIndex: 9999,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    fontSize: "14px",
    fontFamily: "Poppins, sans-serif",
  },
};
